import React, { useState, useEffect, Suspense } from 'react';
import { 
  BrowserRouter as Router,
  Routes,
  Route,
  Link as RouterLink,
  useLocation
} from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  IconButton,
  Toolbar,
  Typography,
  Button,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useScrollTrigger,
  Fade,
  Fab
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Code as ProjectsIcon,
  Person as AboutIcon,
  Mail as ContactIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon
} from '@mui/icons-material';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';

// Import pages
import Home from './pages/Home';
import Projects from './pages/Projects';
import ProjectDetails from './pages/ProjectDetails';
import About from './pages/About';
import Contact from './pages/Contact';
import Footer from './components/Footer';

// Scroll to top button
function ScrollTop() {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <Fade in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
      >
        <Fab color="primary" size="small" aria-label="scroll back to top">
          <KeyboardArrowUpIcon />
        </Fab>
      </Box>
    </Fade>
  );
}

// Scroll to top on route change
function ScrollToTopOnRouteChange() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [pathname]);
  return null;
}

const navItems = [
  { name: 'Home', path: '/', icon: <HomeIcon /> },
  { name: 'Projects', path: '/projects', icon: <ProjectsIcon /> },
  { name: 'About', path: '/about', icon: <AboutIcon /> },
  { name: 'Contact', path: '/contact', icon: <ContactIcon /> },
];

// Theme toggle button component
const ThemeToggleButton = () => {
  const { mode, toggleTheme } = useTheme();
  
  return (
    <IconButton 
      onClick={toggleTheme} 
      aria-label="toggle theme"
      sx={{
        color: mode === 'dark' ? 'primary.main' : 'primary.main',
        backgroundColor: mode === 'light' ? 'rgba(10, 140, 106, 0.08)' : 'rgba(100, 255, 218, 0.05)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'rotate(30deg)',
          backgroundColor: mode === 'light' ? 'rgba(10, 140, 106, 0.15)' : 'rgba(100, 255, 218, 0.1)'
        }
      }}
    >
      {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
    </IconButton>
  );
};

function AppContent() {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const NavigationLinks = () => {
    const { pathname } = useLocation();
    const { mode } = useTheme();
    
    return (
      <>
        {navItems.map((item) => (
          <Button
            key={item.name}
            component={RouterLink}
            to={item.path}
            color="inherit"
            sx={{
              mx: 1,
              fontWeight: 700,
              fontSize: { xs: '0.85rem', md: '0.95rem' },
              position: 'relative',
              color: pathname === item.path ? 'primary.main' : 'text.primary',
              '&::after': pathname === item.path ? {
                content: '""',
                position: 'absolute',
                bottom: -5,
                left: '25%',
                width: '50%',
                height: 3,
                backgroundColor: 'primary.main',
                borderRadius: 1.5
              } : {}
            }}
          >
            {item.name}
          </Button>
        ))}
      </>
    );
  };

  const drawer = (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          KHD
        </Typography>
        <ThemeToggleButton />
      </Box>
      <List>
        {navItems.map((item) => (
          <ListItem key={item.name} disablePadding>
            <ListItemButton
              component={RouterLink}
              to={item.path}
              onClick={handleDrawerToggle}
              sx={{ textAlign: 'center' }}
            >
              <ListItemIcon sx={{ minWidth: 40, justifyContent: 'center' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.name} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Router>
      <ScrollToTopOnRouteChange />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <CssBaseline />
        <AppBar 
          position="sticky" 
          elevation={0}
          sx={{ 
            backdropFilter: 'blur(10px)',
            bgcolor: (theme) => 
              theme.palette.mode === 'dark' 
                ? 'rgba(10, 25, 47, 0.85)' 
                : 'rgba(247, 247, 247, 0.85)'
          }}
        >
          <Container maxWidth="lg">
            <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
              <Typography
                variant="h6"
                component={RouterLink}
                to="/"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                KHD
              </Typography>
              
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <NavigationLinks />
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ThemeToggleButton />
                
                <Box sx={{ display: { xs: 'none', md: 'block' }, ml: 2 }}>
                  <Button 
                    component={RouterLink} 
                    to="/contact"
                    variant="outlined" 
                    color="primary"
                    size="small"
                  >
                    Get In Touch
                  </Button>
                </Box>
                
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="end"
                  onClick={handleDrawerToggle}
                  sx={theme => ({
                    display: { md: 'none' },
                    ml: 1,
                    backgroundColor: theme.palette.mode === 'light' ? 'rgba(10, 140, 106, 0.08)' : 'rgba(100, 255, 218, 0.05)',
                    color: theme.palette.mode === 'light' ? theme.palette.primary.main : 'inherit',
                    border: theme.palette.mode === 'light' ? '1px solid #0a8c6a' : 'none',
                    transition: 'all 0.3s',
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'light' ? 'rgba(10, 140, 106, 0.15)' : 'rgba(100, 255, 218, 0.1)',
                    },
                  })}
                >
                  <MenuIcon sx={theme => ({ color: theme.palette.mode === 'light' ? theme.palette.primary.main : 'inherit' })} />
                </IconButton>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
        
        <Box component="nav">
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            anchor="right"
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: 240,
                backgroundColor: 'background.default'
              },
            }}
          >
            {drawer}
          </Drawer>
        </Box>
        
        <Box component="main" sx={{ flexGrow: 1 }}>
          <Suspense fallback={<Box sx={{ py: 10, textAlign: 'center' }}><Typography variant="h4">Loading...</Typography></Box>}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:id" element={<ProjectDetails />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
            </Routes>
          </Suspense>
        </Box>

        <Box component="footer">
          <Footer />
        </Box>
        
        <ScrollTop />
      </Box>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
